import React, { useMemo, useState, useContext } from 'react';
import { App, Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from 'react-query';
import { getBsaBindingPcsList, isRack, isStack, Pack, Rack, Stack } from '@/api/bsa';
import {
  DeviceData,
  getDeviceDataInstantaneous,
  getDeviceDataInstantaneousFor1m,
  getDeviceDataInstantaneousForRaw,
  GrainChoice,
} from '@/api/device';
import { isNil } from 'lodash-es';
import { timeGapMap } from './BatteryTemper';
import { StorageContext } from '../StorageContext';
import { Checkbox, DatePicker, Empty, Radio, Select, EchartsWithTheme, useTheme } from '@maxtropy/components';

const { RangePicker } = DatePicker;

export interface SocTabProps {
  bsaId: number;
}

enum TimeChoice {
  TODAY,
  LAST_24_HOURS,
  CUSTOM,
}

enum LevelChoice {
  STACK,
  RACK,
  PACK,
}
// 电池堆
const STACK_SOC_PROPERTY_ID = 10055;
// 电池簇
const RACK_SOC_PROPERTY_ID = 10054;
// const PACK_SOC_PROPERTY_ID = 10056;
const POWER_PROPERTY_ID = 10061;
const ALL_OPTION_VALUE = -1;

const MaxselectNums = 10;
const TipsMsg = `最多选择${MaxselectNums}个`;
const SelectMsg = `全选（前${MaxselectNums}个）`;

const SocTabContent: React.FC<SocTabProps> = props => {
  const { bsaId } = props;
  const StorageContextValue = useContext(StorageContext);
  const [customDateRange, setCustomDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | undefined>(
    StorageContextValue?.customDateRange
  );
  const { message } = App.useApp();
  const theme = useTheme();

  const disabledDate = (current: Dayjs) => {
    return current && current.valueOf() >= dayjs().endOf('day').valueOf();
  };

  // 当前颗粒度

  const [from, to] = useMemo(() => {
    if (StorageContextValue?.timeChoice === TimeChoice.TODAY) {
      return [dayjs().startOf('day').valueOf(), dayjs().endOf('day').valueOf()];
    } else if (StorageContextValue?.timeChoice === TimeChoice.LAST_24_HOURS) {
      return [dayjs().subtract(24, 'hours').valueOf(), dayjs().valueOf()];
    } else if (StorageContextValue?.customDateRange) {
      return [
        StorageContextValue?.customDateRange[0].startOf('day').valueOf(),
        StorageContextValue?.customDateRange[1].endOf('day').valueOf(),
      ];
    }
    return [undefined, undefined];
  }, [StorageContextValue?.customDateRange, StorageContextValue?.timeChoice]);

  const { data: pcsList } = useQuery(['pcsList', bsaId], () => getBsaBindingPcsList(bsaId!), {
    enabled: !!bsaId,
  });

  const stacks = StorageContextValue?.stacks;
  const racks = StorageContextValue?.racks;
  // const { data: packs } = useQuery(["packs", bsaId], () => getPacks(bsaId));

  const [selectedPacks] = useState<number[]>([]);

  const pcsComparable =
    StorageContextValue?.levelChoice === LevelChoice.STACK ||
    (StorageContextValue?.levelChoice === LevelChoice.RACK && StorageContextValue?.selectedRacks.length === 1) ||
    (StorageContextValue?.levelChoice === LevelChoice.PACK && selectedPacks.length === 1);

  // 设备ids
  const devicesToQuerySoc = useMemo(() => {
    // if (StorageContextValue?.levelChoice === LevelChoice.STACK && stacks) {
    //   return stacks;
    // }

    if (
      StorageContextValue?.levelChoice === LevelChoice.STACK &&
      stacks &&
      StorageContextValue?.selectedStacks &&
      StorageContextValue?.selectedStacks.length
    ) {
      return StorageContextValue?.selectedStacks.map(id => stacks.find(item => id === item.stackId)!);
    }

    if (racks && StorageContextValue?.selectedRacks && StorageContextValue?.selectedRacks.length) {
      return StorageContextValue?.selectedRacks.map(id => racks.find(item => id === item.rackId)!);
    }
  }, [
    StorageContextValue?.levelChoice,
    StorageContextValue?.selectedStacks,
    StorageContextValue?.selectedRacks,
    stacks,
    racks,
  ]);

  const deviceIds = useMemo(() => {
    if (devicesToQuerySoc && devicesToQuerySoc.length !== 0) {
      if ((devicesToQuerySoc as any).includes(undefined)) {
        return [];
      }
      return devicesToQuerySoc?.map(item => item?.deviceId);
    } else {
      return [];
    }
  }, [devicesToQuerySoc]);

  const requestApi = useMemo(() => {
    return StorageContextValue?.grainChoice === GrainChoice.ORIGINAL
      ? getDeviceDataInstantaneousForRaw
      : StorageContextValue?.grainChoice === GrainChoice.MINUTE_1
      ? getDeviceDataInstantaneousFor1m
      : getDeviceDataInstantaneous;
  }, [StorageContextValue?.grainChoice]);

  const { data: socData, isLoading: isSocDataLoading } = useQuery(
    ['soc', deviceIds, from, to, StorageContextValue?.levelChoice, bsaId, StorageContextValue?.grainChoice],
    () =>
      requestApi({
        deviceIds: deviceIds!,
        propertyIds: [
          StorageContextValue?.levelChoice === LevelChoice.RACK ? RACK_SOC_PROPERTY_ID : STACK_SOC_PROPERTY_ID,
        ],
        startTime: from!,
        endTime: to!,
      }),
    {
      enabled: !!from && !!to && !!deviceIds.length && !!bsaId,
    }
  );

  const pcs = pcsList?.find(item => item.id === devicesToQuerySoc?.[0]?.pcsId);
  const pcsDeviceId = pcs?.deviceId;
  const { data: pcsPowerData, isLoading: isPowerLoading } = useQuery(
    [
      'power',
      pcsDeviceId,
      from,
      to,
      StorageContextValue?.comparePcs,
      pcsComparable,
      StorageContextValue?.levelChoice,
      StorageContextValue?.grainChoice,
    ],
    () =>
      requestApi({
        deviceIds: [pcsDeviceId!],
        propertyIds: [POWER_PROPERTY_ID],
        startTime: from!,
        endTime: to!,
      }),
    {
      enabled: !!bsaId && pcsComparable && StorageContextValue?.comparePcs && !!from && !!to && !!pcsDeviceId,
    }
  );

  // 下拉选项
  const options =
    StorageContextValue?.levelChoice === LevelChoice.STACK
      ? stacks?.map(item => ({
          value: item.stackId,
          label: `电池堆${item.stackSequence + 1}`,
        }))
      : racks?.map(item => ({
          value: item.rackId,
          label:
            (stacks?.length && stacks.length > 1 ? `电池堆${item.stackSequence + 1}` : '') +
            `电池簇${item.rackSequence + 1}`,
        }));

  const selectOptions =
    !options || options.length === 0 ? [] : [{ value: ALL_OPTION_VALUE, label: SelectMsg }, ...options];

  const isDataEmpty = useMemo(() => {
    return (
      !socData?.some(item => item.values.some(v => !isNil(v.value))) &&
      !pcsPowerData?.some(item => item.values.some(v => !isNil(v.value)))
    );
  }, [pcsPowerData, socData]);

  return (
    <div>
      <Space wrap size={20}>
        <Space size={20}>
          <Radio.Group
            buttonStyle={'solid'}
            value={StorageContextValue?.timeChoice}
            onChange={e => {
              StorageContextValue?.setTimeChoice(e.target.value);
            }}
          >
            <Radio.Button value={TimeChoice.TODAY}>当日</Radio.Button>
            <Radio.Button value={TimeChoice.LAST_24_HOURS}>最近24小时</Radio.Button>
            <Radio.Button value={TimeChoice.CUSTOM}>其他</Radio.Button>
          </Radio.Group>
          <Space>
            颗粒度:
            <Radio.Group
              buttonStyle={'solid'}
              value={StorageContextValue?.grainChoice}
              onChange={e => {
                StorageContextValue?.setGrainChoice(e.target.value);
              }}
            >
              <Radio.Button value={GrainChoice.ORIGINAL}>原始数据</Radio.Button>
              <Radio.Button value={GrainChoice.MINUTE_1}>1分钟</Radio.Button>
              <Radio.Button value={GrainChoice.MINUTE_15}>15分钟</Radio.Button>
            </Radio.Group>
          </Space>
          {StorageContextValue?.timeChoice === TimeChoice.CUSTOM && (
            <Space>
              选择日期:
              <RangePicker
                allowClear={false}
                value={customDateRange}
                disabledDate={disabledDate}
                onChange={e => {
                  const value = e as [dayjs.Dayjs, dayjs.Dayjs];
                  const timeGap = value[1].endOf('day').diff(value[0].startOf('day'), 'days', true);
                  if (timeGap > timeGapMap[StorageContextValue?.grainChoice]) {
                    return message.error(`最大跨度为${timeGapMap[StorageContextValue?.grainChoice]}天`);
                  }
                  setCustomDateRange(value);
                  StorageContextValue?.setCustomDateRange(value);
                }}
              />
            </Space>
          )}
        </Space>
        <Space size={20}>
          <Radio.Group
            buttonStyle={'solid'}
            value={StorageContextValue?.levelChoice}
            onChange={e => {
              StorageContextValue?.setComparePcs(false);
              StorageContextValue?.setLevelChoice(e.target.value);
            }}
          >
            <Radio.Button value={LevelChoice.STACK}>电池堆</Radio.Button>
            <Radio.Button value={LevelChoice.RACK}>电池簇</Radio.Button>
            {/* <Radio.Button value={TimeChoice.CUSTOM}>电池组曲线</Radio.Button> */}
          </Radio.Group>
          {StorageContextValue?.levelChoice === LevelChoice.STACK && (
            <Space>
              选择电池堆:
              <Select
                mode={'multiple'}
                allowClear
                placeholder={'请选择'}
                style={{ width: 300 }}
                maxTagCount={2}
                value={StorageContextValue?.selectedStacks}
                options={selectOptions}
                onChange={values => {
                  if (values.includes(ALL_OPTION_VALUE)) {
                    StorageContextValue?.setSelectedStacks(stacks!.map(item => item.stackId).slice(0, MaxselectNums));
                  } else {
                    if (values.length > MaxselectNums) {
                      return message.error(TipsMsg);
                    }
                    StorageContextValue?.setSelectedStacks(values);
                  }
                }}
              />
            </Space>
          )}
          {StorageContextValue?.levelChoice === LevelChoice.RACK && (
            <Space>
              选择电池簇:
              <Select
                mode={'multiple'}
                allowClear
                placeholder={'请选择'}
                style={{ width: 300 }}
                maxTagCount={2}
                value={StorageContextValue?.selectedRacks}
                options={selectOptions}
                onChange={values => {
                  if (values.includes(ALL_OPTION_VALUE)) {
                    StorageContextValue?.setSelectedRacks(racks!.map(item => item.rackId).slice(0, MaxselectNums));
                  } else {
                    if (values.length > MaxselectNums) {
                      return message.error(TipsMsg);
                    }
                    StorageContextValue?.setSelectedRacks(values);
                  }
                }}
              ></Select>
            </Space>
          )}
          <Checkbox
            disabled={!pcsComparable}
            checked={StorageContextValue?.comparePcs}
            onChange={e => {
              StorageContextValue?.setComparePcs(e.target.checked);
            }}
          >
            与PCS功率对比
          </Checkbox>
        </Space>
      </Space>
      <Spin spinning={isPowerLoading || isSocDataLoading}>
        <div style={{ marginTop: 30, height: 'calc(100vh - 303px)' }}>
          {isDataEmpty && !isPowerLoading && !isSocDataLoading ? (
            <Empty style={{ paddingTop: 120 }} />
          ) : (
            <EchartsWithTheme
              option={getOption(
                from!,
                to!,
                !!(stacks?.length && stacks.length > 1),
                socData,
                devicesToQuerySoc,
                pcsPowerData,
                StorageContextValue?.levelChoice
              )}
              theme={theme}
              style={{ height: '100%' }}
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

const getOption = (
  from: number,
  to: number,
  showStackName: boolean,
  socData?: DeviceData[],
  devicesToQuerySoc?: (Stack | Rack | Pack)[],
  pcsPowerData?: DeviceData[],
  levelChoice?: LevelChoice
) => {
  const socSeries =
    socData?.map(item => {
      let name;
      if (item.deviceId === -1) {
        name = '储能系统SOC';
      } else {
        const device = devicesToQuerySoc?.find(d => d.deviceId === item.deviceId)!;
        if (isRack(device))
          name = `${showStackName ? `电池堆${device.stackSequence + 1}` : ''}电池簇${device.rackSequence + 1}SOC`;
        else if (isStack(device)) name = `${showStackName ? `电池堆${device.stackSequence + 1}` : ''}SOC`;
        else name = '储能系统SOC';
      }
      const data = item.values.map(v => [v.time, v.value]);
      return {
        connectNulls: true,
        name,
        type: 'line',
        data,
        smooth: true,
        yAxisIndex: 0,
      };
    }) || [];

  const powerSeries =
    pcsPowerData?.map(item => {
      return {
        connectNulls: true,
        name: 'PCS功率',
        type: 'line',
        data: item.values.map(v => [v.time, v.value]),
        smooth: true,
        yAxisIndex: 1,
      };
    }) || [];

  const socYAxis = {
    name: '%',
    type: 'value',
    scale: true,
  };

  const powerYAxis = {
    name: 'kW',
    type: 'value',
    scale: true,
  };

  return {
    legend: {
      show: true,
      right: 20,
      top: 0,
      icon: 'rect',
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) => {
        return params.reduce(
          (acc, curr) =>
            acc +
            `<span >${curr.marker} ${curr.seriesName}：</span> ${curr.data[1]?.toFixed(1) || '--'}${
              curr.seriesName === 'PCS功率' ? 'kW' : '%'
            }<br />`,
          `${params[0].axisValueLabel}<br />`
        );
      },
    },
    dataZoom: [
      {
        type: 'slider',
        start: levelChoice === LevelChoice.STACK ? 0 : 60,
        end: 100,
        bottom: 15,
        height: 20,
        textStyle: {
          fontSize: 10,
        },
      },
      { type: 'inside' },
    ],
    xAxis: {
      // min: from,
      // max: to,
      // interval: 3600 * 1000,
      minInterval: 60 * 1000,
      type: 'time',
      axisLabel: {
        formatter: (val: number) => dayjs(val, 'x').format('MM-DD[\n]HH:mm'),
        // formatter: (val: number) =>
        //   dayjs(val).format('mm').includes('00') ? dayjs(val).format('H点') : dayjs(val).format('H点mm分'),
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: powerSeries.length ? [socYAxis, powerYAxis] : socYAxis,
    series: [...socSeries, ...powerSeries],
  };
};

export default SocTabContent;
